import React, { FC } from 'react';
import { Button } from '@clearcover/atlas';
import { Modal } from 'Shared/components';
import { PostAttemptResponse } from 'Rewards/services/endpoints/PostAttempt/types';
import styles from './OutcomeModal.module.css';

type OutcomeModalProps = {
  result: PostAttemptResponse;
  /**
   * True when the outcome is a winning tier (config `is_win: true`). Derived
   * from the program config by the caller, not inferred from `result.outcome`
   * — the backend returns tier codes (LARGE/MEDIUM/SMALL/SUPER/NONE/CONSOLATION),
   * never a coarse "WIN" discriminator.
   */
  isWin: boolean;
  /**
   * True when the outcome is a non-win that grants a bonus entry (currently
   * `code === 'CONSOLATION'` in ACT1/ACT2). Drives the "Bonus Entry!" badge
   * styling. Non-win outcomes without a bonus entry (e.g. `NONE` in ACT3)
   * set this to false and fall through to the standard no-prize branch.
   */
  isConsolation: boolean;
  /** Heading shown in the modal — sourced from the config's display metadata. */
  outcomeHeading?: string;
  /** Body copy for the outcome — sourced from the config's display metadata. */
  outcomeDescription?: string;
  /** Prize tier name (e.g. "Super", "Small") — rendered below the dollar amount in win branch. Omit to leave win rendering unchanged (spin path). */
  prizeTierLabel?: string;
  hasRemainingEntries: boolean;
  remainingEntries: number;
  /** Override for the primary CTA label. Defaults to "Play Another Entry (N left)". */
  primaryCtaLabel?: string;
  onClose: () => void;
  onSpinAgain: () => void;
};

export const OutcomeModal: FC<OutcomeModalProps> = ({
  result,
  isWin,
  isConsolation,
  outcomeHeading,
  outcomeDescription,
  prizeTierLabel,
  hasRemainingEntries,
  remainingEntries,
  primaryCtaLabel,
  onClose,
  onSpinAgain,
}) => {
  const showWinPrize = isWin && result.prizeAmount != null;

  const modalClass = showWinPrize
    ? styles.modalWin
    : isConsolation
      ? styles.modalConsolation
      : styles.modalNoWin;

  return (
    <Modal showModal onClose={onClose} className={modalClass}>
      <div className={styles.content}>
        {showWinPrize ? (
          <>
            <span className={styles.emoji} aria-hidden="true">
              🎉
            </span>

            <p className={styles.badgeWin}>You won!</p>

            <h2 className={styles.heading} data-testid="outcome-heading">
              ${result.prizeAmount}
            </h2>

            {prizeTierLabel && (
              <p className={styles.subheading} data-testid="prize-tier-label">
                {prizeTierLabel}
              </p>
            )}
          </>
        ) : isConsolation ? (
          <>
            <span className={styles.emoji} aria-hidden="true">
              ⚡
            </span>

            <p className={styles.badgeConsolation}>Bonus Entry!</p>

            <h2 className={styles.heading} data-testid="outcome-heading">
              {outcomeHeading ?? '+1 Entry'}
            </h2>
          </>
        ) : (
          <>
            <p className={styles.badgeNoWin}>Better luck next time</p>

            <h2 className={styles.heading} data-testid="outcome-heading">
              {outcomeHeading ?? 'No prize this spin'}
            </h2>
          </>
        )}

        {outcomeDescription && (
          <p className={styles.subheading}>{outcomeDescription}</p>
        )}

        {remainingEntries > 0 && (
          <div className={styles.entriesRow}>
            <span className={styles.entriesLabel}>Entries remaining:</span>
            <span className={styles.entriesBadge}>{remainingEntries}</span>
          </div>
        )}

        {hasRemainingEntries && (
          <Button
            variant="primary"
            className={styles.primaryButton}
            onClick={onSpinAgain}
            data-testid="outcome-cta"
          >
            {primaryCtaLabel ?? `Play Another Entry (${remainingEntries} left)`}
          </Button>
        )}

        <Button
          variant="secondary"
          className={styles.secondaryButton}
          onClick={onClose}
          data-testid="outcome-close"
        >
          Return to Portal
        </Button>
      </div>
    </Modal>
  );
};
